import { AggregateStatusEnumOptions } from "@boundless/bl-api-client/partners/models/AggregateStatusEnumLabels"
import { usePartnersReferralsListSuspense } from "@boundless/bl-api-client/partners/services"
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table"
import DatePicker from "react-datepicker"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { partnerReferralColumns } from "@/lib/data-tables/partner-referral-columns"

import type { Referral } from "@boundless/bl-api-client/partners/models"

export function PartnerReferralsTable() {
  const { data: referrals } = usePartnersReferralsListSuspense()

  const table = useReactTable<Referral>({
    data: referrals,
    columns: partnerReferralColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [{ id: "createdAt", desc: true }],
    },
  })

  const getDateFilter = (
    id: string,
  ): { from: Date | null; to: Date | null } => {
    return (
      (table.getColumn(id)?.getFilterValue() as {
        from: Date | null
        to: Date | null
      }) ?? {
        from: null,
        to: null,
      }
    )
  }

  const setDateFilter = (
    id: string,
    updates: Partial<{ from: Date | null; to: Date | null }>,
  ): void => {
    const current = getDateFilter(id)
    table.getColumn(id)?.setFilterValue({
      from: updates.from ?? current.from,
      to: updates.to ?? current.to,
    })
  }

  const legalNamesOptions = referrals.map((referral) => ({
    value: referral.legalName,
    label: referral.legalName,
  }))

  return (
    <>
      <div className="mb-4 grid grid-cols-7 gap-4">
        <div className="space-y-2">
          <Label className="text-base font-medium">Company</Label>
          <Select
            value={
              (table.getColumn("legalName")?.getFilterValue() as string) ?? ""
            }
            onValueChange={(value) =>
              table.getColumn("legalName")?.setFilterValue(value || undefined)
            }
          >
            <SelectTrigger className="!h-9 w-full">
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {legalNamesOptions.map((option, index) => (
                  <SelectItem key={index} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium">Status</Label>
          <Select
            value={
              (table
                .getColumn("aggregateStatus")
                ?.getFilterValue() as string) ?? ""
            }
            onValueChange={(value) =>
              table
                .getColumn("aggregateStatus")
                ?.setFilterValue(value || undefined)
            }
          >
            <SelectTrigger className="!h-9 w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {AggregateStatusEnumOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value ?? ""}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2 space-y-2">
          <Label className="text-base font-medium">Created</Label>
          <div className="flex space-x-2">
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="From"
                selected={getDateFilter("createdAt").from}
                onChange={(date) => setDateFilter("createdAt", { from: date })}
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="To"
                selected={getDateFilter("createdAt").to}
                onChange={(date) => setDateFilter("createdAt", { to: date })}
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
          </div>
        </div>

        <div className="col-span-2 space-y-2">
          <Label className="text-base font-medium">Last Modified</Label>
          <div className="flex space-x-2">
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="From"
                selected={getDateFilter("lastModifiedAt").from}
                onChange={(date) =>
                  setDateFilter("lastModifiedAt", { from: date })
                }
                dateFormat="yyyy-MM-dd"
                wrapperClassName="!w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="To"
                selected={getDateFilter("lastModifiedAt").to}
                onChange={(date) =>
                  setDateFilter("lastModifiedAt", { to: date })
                }
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
          </div>
        </div>

        <div className="flex items-end">
          <Button
            variant="outline"
            className="h-9 w-full"
            onClick={() => table.resetColumnFilters()}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id} className="w-40">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={partnerReferralColumns.length}
                className="h-24 text-center"
              >
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  )
}

export function FallbackReferralsTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Funding Requests</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 3 }).map((_, i) => (
          <TableRow key={i}>
            {Array.from({ length: partnerReferralColumns.length }).map(
              (_, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-6" />
                </TableCell>
              ),
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
